import { QueryClient, queryOptions } from '@tanstack/react-query'
import { ApiError, isRetryable } from './errors'
import { authApi, orgsApi } from './endpoints/auth'
import { authKeysApi } from './endpoints/authKeys'
import { billingApi } from './endpoints/billing'
import { consoleApi } from './endpoints/console'
import { devicesApi } from './endpoints/devices'
import { notifyApi } from './endpoints/notify'
import { statusApi } from './endpoints/status'
import { supportApi } from './endpoints/support'
import { tenantsApi } from './endpoints/tenants'

/**
 * Query keys, in one place so invalidation cannot drift from the definitions.
 */
export const queryKeys = {
  me: ['auth', 'me'] as const,
  sessions: ['auth', 'sessions'] as const,
  orgMembers: (orgId: string) => ['orgs', orgId, 'members'] as const,
  authKeys: ['auth-keys'] as const,
  devices: ['devices'] as const,
  billingUsage: ['billing', 'usage'] as const,
  billingInvoices: ['billing', 'invoices'] as const,
  tickets: ['support', 'tickets'] as const,
  account: ['account'] as const,
  quota: ['account', 'quota'] as const,
  members: ['account', 'members'] as const,
  notifications: (unread?: boolean) => ['notifications', { unread }] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
  notificationPreferences: ['notifications', 'preferences'] as const,
  statusComponents: ['status', 'components'] as const,
  statusIncidents: ['status', 'incidents'] as const,
  statusUptime: ['status', 'uptime'] as const,
  databases: ['console', 'databases'] as const,
  tables: (databaseId: string) => ['console', 'tables', databaseId] as const,
  table: (databaseId: string, name: string) => ['console', 'table', databaseId, name] as const,
  snapshots: (databaseId: string) => ['console', 'snapshots', databaseId] as const,
}

/* ------------------------------------------------------------------------ */

export const authQueries = {
  /**
   * The signed-in user. `retry: false` because a 401 here is a definitive
   * answer, not a transient failure — the interceptors already tried refreshing.
   */
  me: () =>
    queryOptions({
      queryKey: queryKeys.me,
      queryFn: () => authApi.me(),
      staleTime: 5 * 60 * 1000,
      retry: false,
    }),

  sessions: () =>
    queryOptions({
      queryKey: queryKeys.sessions,
      queryFn: () => authApi.listSessions(),
      staleTime: 30 * 1000,
    }),

  orgMembers: (orgId: string) =>
    queryOptions({
      queryKey: queryKeys.orgMembers(orgId),
      queryFn: () => orgsApi.listMembers(orgId),
    }),
}

export const authKeyQueries = {
  list: () =>
    queryOptions({
      queryKey: queryKeys.authKeys,
      queryFn: () => authKeysApi.list(),
      staleTime: 30 * 1000,
    }),
}

export const deviceQueries = {
  /**
   * Polled every 10s so a pending approval appears without a manual refresh.
   * This is the fallback path when the push channel is unavailable, which is
   * why the interval is short rather than the socket being the only route.
   */
  list: () =>
    queryOptions({
      queryKey: queryKeys.devices,
      queryFn: () => devicesApi.list(),
      refetchInterval: 10_000,
      refetchOnWindowFocus: true,
      staleTime: 5_000,
    }),
}

export const billingQueries = {
  usage: () =>
    queryOptions({
      queryKey: queryKeys.billingUsage,
      queryFn: () => billingApi.usage(),
      staleTime: 60 * 1000,
    }),

  invoices: () =>
    queryOptions({
      queryKey: queryKeys.billingInvoices,
      queryFn: () => billingApi.invoices(),
      staleTime: 60 * 1000,
    }),
}

export const supportQueries = {
  tickets: () =>
    queryOptions({
      queryKey: queryKeys.tickets,
      queryFn: () => supportApi.listTickets(),
      staleTime: 30 * 1000,
    }),
}

export const statusQueries = {
  components: () =>
    queryOptions({
      queryKey: queryKeys.statusComponents,
      queryFn: () => statusApi.components(),
      staleTime: 30 * 1000,
    }),

  incidents: () =>
    queryOptions({
      queryKey: queryKeys.statusIncidents,
      queryFn: () => statusApi.incidents(),
      staleTime: 30 * 1000,
    }),

  uptime: () =>
    queryOptions({
      queryKey: queryKeys.statusUptime,
      queryFn: () => statusApi.uptime(),
      staleTime: 5 * 60 * 1000,
    }),
}

/**
 * Console queries. Backed by the mock adapter until the platform implements
 * the endpoints — see docs/PLATFORM_API_GAPS.md.
 */
export const consoleQueries = {
  databases: () =>
    queryOptions({
      queryKey: queryKeys.databases,
      queryFn: () => consoleApi.listDatabases(),
      staleTime: 5 * 60 * 1000,
    }),

  tables: (databaseId: string) =>
    queryOptions({
      queryKey: queryKeys.tables(databaseId),
      queryFn: () => consoleApi.listTables(databaseId),
      staleTime: 30 * 1000,
    }),

  table: (databaseId: string, tableName: string) =>
    queryOptions({
      queryKey: queryKeys.table(databaseId, tableName),
      queryFn: () => consoleApi.getTable(databaseId, tableName),
      staleTime: 30 * 1000,
    }),

  snapshots: (databaseId: string) =>
    queryOptions({
      queryKey: queryKeys.snapshots(databaseId),
      queryFn: () => consoleApi.listSnapshots(databaseId),
      staleTime: 60 * 1000,
    }),
}

/* ------------------------------------------------------------------------ */

/**
 * Tenant self-service and in-app notifications (services/tenants,
 * services/notify).
 */
export const tenantQueries = {
  account: () =>
    queryOptions({
      queryKey: queryKeys.account,
      queryFn: () => tenantsApi.getAccount(),
      staleTime: 60 * 1000,
    }),

  quota: () =>
    queryOptions({
      queryKey: queryKeys.quota,
      queryFn: () => tenantsApi.getQuota(),
      // Short staleness: this feeds the quota banner, which must not lag.
      staleTime: 15 * 1000,
      refetchInterval: 60 * 1000,
    }),

  members: () =>
    queryOptions({
      queryKey: queryKeys.members,
      queryFn: () => tenantsApi.listMembers(),
    }),
}

export const notificationQueries = {
  list: (options: { unread?: boolean; limit?: number } = {}) =>
    queryOptions({
      queryKey: queryKeys.notifications(options.unread),
      queryFn: () => notifyApi.list(options),
      staleTime: 15 * 1000,
    }),

  /** The header badge. Cheap, and polled rather than subscribed in pass 1. */
  unreadCount: () =>
    queryOptions({
      queryKey: queryKeys.unreadCount,
      queryFn: () => notifyApi.unreadCount(),
      refetchInterval: 30 * 1000,
    }),

  preferences: () =>
    queryOptions({
      queryKey: queryKeys.notificationPreferences,
      queryFn: () => notifyApi.preferences(),
      staleTime: 60 * 1000,
    }),
}

/* ------------------------------------------------------------------------ */

/** Shared client with defaults tuned for a dashboard. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // One retry for transient failures, none for 4xx — those will not
        // fix themselves and a second round trip just delays the error.
        retry: (failureCount, error) => failureCount < 1 && isRetryable(error),
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        throwOnError: (error) => error instanceof ApiError && error.status === 401,
      },
      mutations: {
        retry: false,
      },
    },
  })
}
